import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { supabase } from '../lib/supabase';
import { Button } from '../components/media/MediaUI';
import { CustomerAvatar } from '../components/shared/PersonAvatar';
import { nationalityToFlag, whatsAppUrl } from '../lib/countries';

// ─────────────────────────────────────────────────────────────────────────────
// WEBHOOK — n8n endpoint that sends the WhatsApp review request.
//     Payload field names must stay exactly customer_id / full_name / phone /
//     language / booking_number: the "Prepare (chosen language)" node reads
//     them by those names, and a rename fails silently.
//     While it is an empty string every send is a no-op: the user gets a
//     "Webhook not configured yet" toast and no row's status is changed.
// ─────────────────────────────────────────────────────────────────────────────
const REVIEW_WEBHOOK_URL: string = 'https://n8n-n8n.gdsddq.easypanel.host/webhook/review-request';

// ─── Types ────────────────────────────────────────────────────────────────────

type ReviewStatus = 'pending_review' | 'awaiting_send' | 'sent' | 'rejected';
type TabKey = 'rate' | 'awaiting' | 'sent';

/** Columns the table can be ordered by. Everything else is display-only. */
type SortKey = 'nationality' | 'age' | 'phone' | 'plate_number' | 'booking_end_date' | 'our_rating';
type SortDir = 'asc' | 'desc';

interface SortState { key: SortKey; dir: SortDir }

/**
 * How each sortable column compares. Text uses localeCompare (numeric-aware, so
 * plate "34 A 9" lands before "34 A 10"), numbers compare numerically, and the
 * booking column compares parsed dates rather than the ISO strings.
 */
const SORT_KINDS: Record<SortKey, 'text' | 'number' | 'date'> = {
  nationality:      'text',
  phone:            'text',
  plate_number:     'text',
  age:              'number',
  our_rating:       'number',
  booking_end_date: 'date',
};

/** Language the outgoing message is written in — chosen per send, not stored. */
type MessageLanguage = 'ar' | 'tr' | 'en';

const LANGUAGE_CHOICES: { code: MessageLanguage; label: string }[] = [
  { code: 'ar', label: 'Arabic'  },
  { code: 'tr', label: 'Turkish' },
  { code: 'en', label: 'English' },
];

interface ReviewCandidate {
  customer_id:           string;   // uuid
  first_name:            string | null;
  last_name:             string | null;
  full_name:             string | null;
  nationality:           string | null;
  phone:                 string | null;
  email:                 string | null;
  language:              string | null;
  birth_date:            string | null;
  age:                   number | null;
  latest_booking_id:     number | null;
  latest_booking_number: string | null;
  booking_start_date:    string | null;
  booking_end_date:      string | null;
  plate_number:          string | null;
  model_name:            string | null;
  review_id:             number | null;
  our_rating:            number | null;
  review_status:         ReviewStatus;
  review_sent_at:        string | null;
}

// ─── De-duplication ───────────────────────────────────────────────────────────

/**
 * The view is one row per customer_id, but the same human is often stored twice
 * under different ids. Collapse on normalised name + digits-only phone, keeping
 * the first occurrence so the view's own ordering decides the winner.
 */
function personKey(r: ReviewCandidate): string {
  const name  = (r.full_name ?? '').trim().toLowerCase().replace(/\s+/g, ' ');
  const phone = (r.phone ?? '').replace(/\D/g, '');
  return `${name}::${phone}`;
}

function dedupeByPerson(rows: ReviewCandidate[]): ReviewCandidate[] {
  const seen = new Set<string>();
  const out: ReviewCandidate[] = [];
  for (const r of rows) {
    const key = personKey(r);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(r);
  }
  return out;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(d: string | null): string {
  if (!d) return '—';
  const dt = new Date(d.length <= 10 ? d + 'T00:00:00' : d);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—';
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return '—';
  return dt.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

function dash(v: string | number | null | undefined): string {
  if (v === null || v === undefined) return '—';
  const s = String(v).trim();
  return s ? s : '—';
}

/**
 * Sortable value for one row, normalised so the comparator never has to know
 * which column it is looking at. Empty strings and unparseable dates collapse
 * to null so they are treated as missing rather than sorting as "".
 */
function sortValue(row: ReviewCandidate, key: SortKey): string | number | null {
  const raw = row[key];
  if (raw == null) return null;
  if (SORT_KINDS[key] === 'date') {
    const ms = new Date(raw as string).getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  if (SORT_KINDS[key] === 'number') {
    const n = Number(raw);
    return Number.isNaN(n) ? null : n;
  }
  const text = String(raw).trim();
  return text === '' ? null : text;
}

/**
 * Orders rows by one column. Missing values always sink to the bottom, in both
 * directions — flipping to descending should not float the blanks to the top.
 * Returns a new array; the caller's list is never mutated.
 */
function sortRows(list: ReviewCandidate[], sort: SortState | null): ReviewCandidate[] {
  if (!sort) return list;
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    const cmp = typeof av === 'number' && typeof bv === 'number'
      ? av - bv
      : String(av).localeCompare(String(bv), undefined, { sensitivity: 'base', numeric: true });
    return cmp * factor;
  });
}

/**
 * Normalises whatever sits in `customers.language` down to a code the workflow
 * understands. Bulk sends have no employee-picked language, and the column
 * holds free text ("Arabic", "TR", null), so anything unrecognised falls back
 * to English rather than reaching the workflow as a value it cannot switch on.
 */
function toMessageLanguage(value: string | null | undefined): MessageLanguage {
  const v = (value ?? '').trim().toLowerCase();
  if (v.startsWith('ar')) return 'ar';
  if (v.startsWith('tr') || v.startsWith('tü') || v.startsWith('tu')) return 'tr';
  return 'en';
}

/**
 * Posts one review request. Never toasts — callers decide how to report.
 * `language` overrides the customer's stored language when the employee picked
 * one; bulk sends pass nothing and fall back to whatever is on the record.
 *
 * The n8n workflow answers `{ success: true }` and does the database update
 * itself, so a 2xx with anything else in the body is treated as a failure.
 */
async function postWebhook(
  row: ReviewCandidate,
  language?: MessageLanguage,
): Promise<{ ok: boolean; error?: string }> {
  if (!REVIEW_WEBHOOK_URL) return { ok: false, error: 'Webhook not configured yet' };
  try {
    const res = await fetch(REVIEW_WEBHOOK_URL, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        customer_id:    row.customer_id,
        full_name:      row.full_name,
        phone:          row.phone,
        language:       language ?? toMessageLanguage(row.language),
        booking_number: row.latest_booking_number,
      }),
    });
    if (!res.ok) return { ok: false, error: `Webhook returned HTTP ${res.status}` };

    // n8n can be configured to answer with no body at all; only an explicit
    // `success: false` (or an error message) counts as a rejected send.
    const text = (await res.text()).trim();
    if (text) {
      try {
        const payload = JSON.parse(text) as { success?: boolean; error?: string; message?: string };
        if (payload && payload.success === false) {
          return { ok: false, error: payload.error ?? payload.message ?? 'Webhook reported a failure' };
        }
      } catch {
        // Non-JSON body from a 2xx response — treat the status code as truth.
      }
    }
    return { ok: true };
  } catch (err) {
    return { ok: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

// ─── Small components ─────────────────────────────────────────────────────────

const Toast: React.FC<{ message: string; kind: 'success' | 'error' }> = ({ message, kind }) =>
  ReactDOM.createPortal(
    <div style={{
      position: 'fixed', bottom: 28, right: 28, zIndex: 2000,
      display: 'flex', alignItems: 'center', gap: 10,
      background: kind === 'success' ? '#0f1117' : '#fff1f2',
      color: kind === 'success' ? '#fff' : '#ef4444',
      border: kind === 'error' ? '1px solid #fecaca' : 'none',
      borderRadius: 12, padding: '12px 18px', fontSize: 13, fontWeight: 500,
      boxShadow: '0 8px 32px rgba(0,0,0,0.18)', animation: 'slideUp 200ms ease',
      maxWidth: 'calc(100vw - 56px)',
    }}>
      {kind === 'success'
        ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" fill="#16a34a" /><path d="M7 12l4 4 6-6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
        : <svg width="16" height="16" viewBox="0 0 24 24" fill="none"><circle cx="12" cy="12" r="10" stroke="#ef4444" strokeWidth="1.8" /><path d="M12 8v5M12 16h.01" stroke="#ef4444" strokeWidth="2" strokeLinecap="round" /></svg>}
      {message}
    </div>,
    document.body,
  );

const StarIcon: React.FC<{ filled: boolean; size?: number }> = ({ filled, size = 19 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    fill={filled ? '#f59e0b' : 'none'}
    stroke={filled ? '#f59e0b' : '#d1d5db'}
    strokeWidth="1.6" strokeLinejoin="round">
    <path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.45l-5.81 3.05 1.11-6.47-4.7-4.58 6.5-.95L12 2.6z" />
  </svg>
);

/** Interactive 1–5 stars. Each star is a 44px-tall target; hover previews. */
const StarRating: React.FC<{
  value:  number | null;
  busy?:  boolean;
  onRate: (rating: number) => void;
}> = ({ value, busy, onRate }) => {
  const [hover, setHover] = useState<number | null>(null);
  const shown = hover ?? value ?? 0;
  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', opacity: busy ? 0.45 : 1 }}
      onMouseLeave={() => setHover(null)}
    >
      {[1, 2, 3, 4, 5].map(n => (
        <button
          key={n}
          type="button"
          disabled={busy}
          title={`${n} star${n > 1 ? 's' : ''}`}
          aria-label={`Rate ${n} star${n > 1 ? 's' : ''}`}
          onMouseEnter={() => { if (!busy) setHover(n); }}
          onClick={() => { if (!busy) onRate(n); }}
          style={{
            width: 32, height: 44, padding: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: 'none', background: 'transparent',
            cursor: busy ? 'wait' : 'pointer',
          }}
        >
          <StarIcon filled={n <= shown} />
        </button>
      ))}
    </div>
  );
};

/**
 * Read-only rendering of a rating already given — used in the Awaiting Send and
 * Sent tabs, where the score is history rather than an input. Shares StarIcon
 * with StarRating so both tabs stay visually identical, just smaller here so
 * the column sits comfortably next to the text cells.
 */
const StarsDisplay: React.FC<{ value: number | null }> = ({ value }) => {
  if (value == null) return <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>;
  return (
    <span
      style={{ display: 'inline-flex', alignItems: 'center', gap: 1, whiteSpace: 'nowrap' }}
      title={`Rated ${value} star${value === 1 ? '' : 's'}`}
      aria-label={`Our rating: ${value} out of 5`}
    >
      {[1, 2, 3, 4, 5].map(n => (
        <StarIcon key={n} filled={n <= value} size={14} />
      ))}
    </span>
  );
};

const WhatsAppIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

/** Phone rendered as a wa.me deep link, or a muted dash when unusable. */
const PhoneCell: React.FC<{ phone: string | null }> = ({ phone }) => {
  const url = whatsAppUrl(phone);
  if (!url) return <span style={{ color: '#d1d5db', fontSize: 13 }}>{dash(phone)}</span>;
  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={e => e.stopPropagation()}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        color: '#16a34a', textDecoration: 'none',
        fontSize: 12.5, fontWeight: 500, whiteSpace: 'nowrap',
        padding: '4px 9px', borderRadius: 7,
        background: 'rgba(22,163,74,0.07)', transition: 'background 140ms ease',
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(22,163,74,0.14)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(22,163,74,0.07)'; }}
    >
      <WhatsAppIcon />
      {phone}
    </a>
  );
};

const NationalityCell: React.FC<{ nationality: string | null }> = ({ nationality }) => {
  const { flag, label } = nationalityToFlag(nationality);
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, whiteSpace: 'nowrap' }}>
      <span style={{ fontSize: 15, lineHeight: 1 }}>{flag}</span>
      <span style={{ fontSize: 13, color: label === '—' ? '#d1d5db' : '#374151' }}>{label}</span>
    </span>
  );
};

const CustomerCell: React.FC<{ name: string | null; email: string | null }> = ({ name, email }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
    <CustomerAvatar alt={name ?? ''} size={32} />
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13.5, fontWeight: 600, color: '#0f1117', whiteSpace: 'nowrap' }}>{dash(name)}</div>
      {email && (
        <div style={{ fontSize: 11.5, color: '#9ca3af', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 200 }}>
          {email}
        </div>
      )}
    </div>
  </div>
);

const PlateCell: React.FC<{ plate: string | null; model: string | null }> = ({ plate, model }) => {
  if (!plate) return <span style={{ color: '#d1d5db', fontSize: 13 }}>—</span>;
  return (
    <div style={{ whiteSpace: 'nowrap' }}>
      <span className="font-mono tabular-nums" style={{
        display: 'inline-block', background: '#f3f4f6', borderRadius: 6,
        padding: '3px 8px', fontSize: 12, fontWeight: 600, color: '#0f1117', letterSpacing: '0.4px',
      }}>
        {plate}
      </span>
      {model && <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 3 }}>{model}</div>}
    </div>
  );
};

const BookingCell: React.FC<{ start: string | null; end: string | null; number: string | null }> = ({ start, end, number }) => {
  const hasRange = !!(start || end);
  return (
    <div style={{ whiteSpace: 'nowrap' }}>
      <div style={{ fontSize: 12.5, color: hasRange ? '#374151' : '#d1d5db' }}>
        {hasRange
          ? <>{formatDate(start)} <span style={{ color: '#3A74C2', margin: '0 3px' }}>→</span> {formatDate(end)}</>
          : '—'}
      </div>
      {number && (
        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: '#9ca3af', marginTop: 3 }}>
          {number}
        </div>
      )}
    </div>
  );
};

const thBase: React.CSSProperties = {
  padding: '12px 16px',
  fontSize: 10.5, fontWeight: 600, color: '#9ca3af',
  letterSpacing: '0.7px', textTransform: 'uppercase',
  whiteSpace: 'nowrap', background: 'var(--surface)',
  borderBottom: '1px solid var(--border-subtle)',
};

/**
 * Column header. Passing `sortKey` turns it into a sort control: clicking sorts
 * ascending, clicking the active column again flips the direction. The arrow
 * slot is always rendered so headers never shift width when a column activates.
 */
const Th: React.FC<{
  children: React.ReactNode;
  align?:   'left' | 'right' | 'center';
  sortKey?: SortKey;
  sort?:    SortState | null;
  onSort?:  (key: SortKey) => void;
}> = ({ children, align = 'left', sortKey, sort, onSort }) => {
  if (!sortKey || !onSort) {
    return <th style={{ ...thBase, textAlign: align }}>{children}</th>;
  }

  const active = sort?.key === sortKey;
  const dir = active ? sort!.dir : null;

  return (
    <th style={{ ...thBase, textAlign: align, padding: 0 }} aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        title={active
          ? `Sorted ${dir === 'asc' ? 'ascending' : 'descending'} — click to reverse`
          : 'Click to sort'}
        style={{
          width: '100%', minHeight: 44, padding: '12px 16px',
          display: 'flex', alignItems: 'center', gap: 5,
          justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
          border: 'none', background: 'transparent', cursor: 'pointer',
          font: 'inherit', fontSize: 10.5, fontWeight: 600,
          letterSpacing: '0.7px', textTransform: 'uppercase',
          color: active ? '#3A74C2' : '#9ca3af',
          transition: 'color 140ms ease',
        }}
        onMouseEnter={e => { if (!active) e.currentTarget.style.color = '#6b7280'; }}
        onMouseLeave={e => { if (!active) e.currentTarget.style.color = '#9ca3af'; }}
      >
        {children}
        <span style={{ width: 8, fontSize: 9, lineHeight: 1, color: '#3A74C2', flexShrink: 0 }}>
          {active ? (dir === 'asc' ? '↑' : '↓') : ''}
        </span>
      </button>
    </th>
  );
};

const td: React.CSSProperties = { padding: '12px 16px', verticalAlign: 'middle' };

/** Primary action button, 44px tall for touch. */
const SendButton: React.FC<{
  label: string;
  busy?: boolean;
  variant?: 'primary' | 'ghost';
  onClick: () => void;
}> = ({ label, busy, variant = 'primary', onClick }) => (
  <Button
    variant={variant === 'primary' ? 'default' : 'outline'}
    disabled={busy}
    onClick={onClick}
    style={{ minHeight: 40, padding: '0 14px', gap: 7, fontSize: 12.5, fontFamily: 'inherit' }}
  >
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
      <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
    {busy ? 'Sending…' : label}
  </Button>
);

/**
 * Asks which language the message should be written in before anything is sent.
 * Nothing is pre-selected — the employee must actively pick, and Cancel sends
 * nothing at all.
 */
const LanguageModal: React.FC<{
  customerName: string | null;
  mode:  'send' | 'resend';
  busy:  boolean;
  onPick: (language: MessageLanguage) => void;
  onClose: () => void;
}> = ({ customerName, mode, busy, onPick, onClose }) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.removeEventListener('keydown', onKey); document.body.style.overflow = prev; };
  }, [onClose, busy]);

  return ReactDOM.createPortal(
    <div
      onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1200,
        background: 'rgba(15,17,23,0.5)', backdropFilter: 'blur(4px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20, animation: 'fadeIn 150ms ease', overflowY: 'auto',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 18, width: '100%', maxWidth: 420,
          boxShadow: '0 24px 80px rgba(0,0,0,0.2)', animation: 'slideUp 180ms ease',
        }}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 600, color: '#0f1117' }}>Choose message language</div>
            <div style={{ fontSize: 12.5, color: '#9ca3af', marginTop: 3 }}>
              {mode === 'resend' ? 'Resending to ' : 'Sending to '}
              <strong style={{ color: '#374151', fontWeight: 600 }}>{customerName ?? 'customer'}</strong>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
            style={{
              width: 34, height: 34, borderRadius: 9, border: 'none', background: '#f3f4f6',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: busy ? 'not-allowed' : 'pointer', flexShrink: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"><path d="M18 6L6 18M6 6l12 12" stroke="#6b7280" strokeWidth="2.2" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Choices — no default selection */}
        <div style={{ padding: '18px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {LANGUAGE_CHOICES.map(choice => (
            <button
              key={choice.code}
              type="button"
              disabled={busy}
              onClick={() => onPick(choice.code)}
              style={{
                minHeight: 52, padding: '0 16px', width: '100%',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                borderRadius: 11, border: '1px solid #EBEBEB', background: '#fff',
                fontFamily: 'inherit', cursor: busy ? 'wait' : 'pointer',
                transition: 'all 140ms ease', textAlign: 'left',
              }}
              onMouseEnter={e => {
                if (busy) return;
                const b = e.currentTarget;
                b.style.borderColor = '#4ba6ea';
                b.style.background = 'rgba(75,166,234,0.05)';
              }}
              onMouseLeave={e => {
                if (busy) return;
                const b = e.currentTarget;
                b.style.borderColor = '#EBEBEB';
                b.style.background = '#fff';
              }}
            >
              <span style={{ fontSize: 15, fontWeight: 600, color: '#0f1117' }}>{choice.label}</span>
              <span style={{
                fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600,
                color: '#6b7280', background: '#f3f4f6', borderRadius: 6, padding: '2px 7px',
                flexShrink: 0,
              }}>
                {choice.code}
              </span>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0 24px 20px' }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              minHeight: 44, padding: '0 18px', borderRadius: 9,
              border: '1px solid #EBEBEB', background: '#fff',
              fontSize: 14, fontWeight: 500, color: 'var(--foreground)',
              cursor: busy ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
            }}
          >
            {busy ? 'Sending…' : 'Cancel'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

// ─── Page ─────────────────────────────────────────────────────────────────────

const TABS: { key: TabKey; label: string }[] = [
  { key: 'rate',     label: 'Rate Customers' },
  { key: 'awaiting', label: 'Awaiting Send'  },
  { key: 'sent',     label: 'Sent'           },
];

const GoogleReviewsPage: React.FC = () => {
  const [rows,    setRows]    = useState<ReviewCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState<string | null>(null);
  const [tab,     setTab]     = useState<TabKey>('rate');
  const [search,  setSearch]  = useState('');

  // Shared across tabs on purpose: switching tabs keeps whatever ordering the
  // user picked. Null means the view's own order (newest booking end first).
  const [sort, setSort] = useState<SortState | null>(null);

  const toggleSort = useCallback((key: SortKey) => {
    setSort(current =>
      current?.key === key
        ? { key, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' });
  }, []);

  const [ratingId,    setRatingId]    = useState<string | null>(null);
  const [sendingId,   setSendingId]   = useState<string | null>(null);
  const [bulkSending, setBulkSending] = useState(false);

  // Which row is waiting on a language choice, and whether it is a first send
  // or a resend. Null means the modal is closed.
  const [langPrompt, setLangPrompt] =
    useState<{ row: ReviewCandidate; mode: 'send' | 'resend' } | null>(null);

  const [toast, setToast] = useState<{ message: string; kind: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, kind: 'success' | 'error' = 'success') => {
    setToast({ message, kind });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // One fetch of the whole view; the three tabs are partitions of it.
  const load = useCallback(async () => {
    const { data, error: fetchError } = await supabase
      .from('review_candidates')
      .select('*')
      .order('booking_end_date', { ascending: false, nullsFirst: false });

    if (fetchError) { setError(fetchError.message); setRows([]); setLoading(false); return; }
    setError(null);
    setRows((data ?? []) as ReviewCandidate[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => { await load(); if (cancelled) return; })();
    return () => { cancelled = true; };
  }, [load]);

  // De-duplicated per tab, so a person stored twice is only actioned once.
  const byStatus = useMemo(() => ({
    rate:     dedupeByPerson(rows.filter(r => r.review_status === 'pending_review')),
    awaiting: dedupeByPerson(rows.filter(r => r.review_status === 'awaiting_send')),
    sent:     dedupeByPerson(rows.filter(r => r.review_status === 'sent')),
  }), [rows]);

  const visible = useMemo(() => {
    const list = byStatus[tab];
    const q = search.trim().toLowerCase();
    const filtered = q
      ? list.filter(r =>
          (r.full_name ?? '').toLowerCase().includes(q) ||
          (r.phone ?? '').toLowerCase().includes(q) ||
          (r.plate_number ?? '').toLowerCase().includes(q) ||
          (r.latest_booking_number ?? '').toLowerCase().includes(q) ||
          (r.nationality ?? '').toLowerCase().includes(q)
        )
      : list;
    return sortRows(filtered, sort);
  }, [byStatus, tab, search, sort]);

  // ── Rating ────────────────────────────────────────────────────────────────
  // 5 stars → worth asking for a Google review; anything less is rejected.
  const handleRate = useCallback(async (row: ReviewCandidate, rating: number) => {
    if (ratingId) return;
    setRatingId(row.customer_id);

    const { data: { user } } = await supabase.auth.getUser();
    const status: ReviewStatus = rating === 5 ? 'awaiting_send' : 'rejected';

    const { error: upsertError } = await supabase
      .from('customer_reviews')
      .upsert({
        customer_id:          row.customer_id,        // uuid — never cast
        reference_booking_id: row.latest_booking_id,
        our_rating:           rating,
        status,
        rated_by:             user?.id ?? null,
        rated_at:             new Date().toISOString(),
      }, { onConflict: 'customer_id' });

    setRatingId(null);
    if (upsertError) { showToast(upsertError.message, 'error'); return; }

    const name = row.full_name ?? 'Customer';
    showToast(
      rating === 5
        ? `${name} rated 5★ — moved to Awaiting Send`
        : `${name} rated ${rating}★ — not eligible for a review request`,
    );
    await load();
  }, [ratingId, showToast, load]);

  // ── Send ──────────────────────────────────────────────────────────────────
  const handleSend = useCallback(async (row: ReviewCandidate, language: MessageLanguage) => {
    if (sendingId || bulkSending) return;
    setSendingId(row.customer_id);

    const result = await postWebhook(row, language);
    if (!result.ok) {
      setSendingId(null);
      setLangPrompt(null);
      showToast(result.error ?? 'Send failed', 'error');
      return;   // status deliberately unchanged
    }

    const { error: updateError } = await supabase
      .from('customer_reviews')
      .update({ status: 'sent', review_sent_at: new Date().toISOString() })
      .eq('customer_id', row.customer_id);

    setSendingId(null);
    setLangPrompt(null);
    // The workflow marks the row sent as well, so a failure here is not fatal —
    // reload either way and let the fresh data decide which tab the row lands in.
    if (updateError) {
      showToast(`Request sent — status not confirmed locally: ${updateError.message}`, 'error');
      await load();
      return;
    }
    showToast(`Review request sent to ${row.full_name ?? 'customer'} in ${language.toUpperCase()}`);
    await load();
  }, [sendingId, bulkSending, showToast, load]);

  // Sequential on purpose — one webhook call at a time rather than a burst.
  const handleSendAll = useCallback(async () => {
    const queue = byStatus.awaiting;
    if (queue.length === 0 || bulkSending) return;
    if (!REVIEW_WEBHOOK_URL) { showToast('Webhook not configured yet', 'error'); return; }

    setBulkSending(true);
    let sent = 0;
    let failed = 0;

    for (const row of queue) {
      const result = await postWebhook(row);
      if (!result.ok) { failed++; continue; }
      const { error: updateError } = await supabase
        .from('customer_reviews')
        .update({ status: 'sent', review_sent_at: new Date().toISOString() })
        .eq('customer_id', row.customer_id);
      if (updateError) failed++; else sent++;
    }

    setBulkSending(false);
    showToast(
      failed === 0
        ? `Sent ${sent} review request${sent === 1 ? '' : 's'}`
        : `Sent ${sent}, ${failed} failed`,
      failed === 0 ? 'success' : 'error',
    );
    await load();
  }, [byStatus, bulkSending, showToast, load]);

  // Resend never mutates anything — it just fires the webhook again.
  const handleResend = useCallback(async (row: ReviewCandidate, language: MessageLanguage) => {
    if (sendingId) return;
    setSendingId(row.customer_id);
    const result = await postWebhook(row, language);
    setSendingId(null);
    setLangPrompt(null);
    if (!result.ok) { showToast(result.error ?? 'Resend failed', 'error'); return; }
    showToast(`Review request resent to ${row.full_name ?? 'customer'} in ${language.toUpperCase()}`);
  }, [sendingId, showToast]);

  /**
   * Row buttons no longer send — they ask for a language first. With no webhook
   * configured there is nothing to choose between, so keep the existing toast
   * and never open the modal.
   */
  const promptLanguage = useCallback((row: ReviewCandidate, mode: 'send' | 'resend') => {
    if (!REVIEW_WEBHOOK_URL) { showToast('Webhook not configured yet', 'error'); return; }
    setLangPrompt({ row, mode });
  }, [showToast]);

  // Every tab now renders 7 columns — the shape differs, the count does not.
  const colCount = 7;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--surface)', padding: '32px 28px 56px' }}>
      <style>{`
        @keyframes pulse   { 0%,100%{opacity:1} 50%{opacity:0.4} }
        @keyframes fadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes slideUp { from{transform:translateY(8px);opacity:0} to{transform:translateY(0);opacity:1} }
        .gr-row { transition: background 120ms ease; }
        .gr-row:hover td { background: #f8fafc; }
        .gr-tabs { display: flex; gap: 8px; flex-wrap: wrap; }
        .gr-toolbar { display: flex; gap: 12px; flex-wrap: wrap; align-items: center; margin-bottom: 18px; }
        .gr-search { position: relative; flex: 1 1 260px; min-width: 0; max-width: 420px; }
      `}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 16, marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#4ba6ea' }} />
            <span style={{ fontSize: 12, fontWeight: 600, color: '#3A74C2', letterSpacing: '0.8px', textTransform: 'uppercase' }}>
              Reputation
            </span>
          </div>
          <h1 style={{ margin: 0, fontSize: 30, fontWeight: 500, letterSpacing: '-0.8px', color: '#0f1117', lineHeight: 1.1 }}>
            Google Reviews
          </h1>
          <p style={{ margin: '6px 0 0', fontSize: 15, color: '#6b7280', lineHeight: 1.5 }}>
            Rate past customers, then send Google Maps review requests to the best ones
          </p>
        </div>

        <div className="gr-tabs">
          {TABS.map(t => {
            const active = tab === t.key;
            const count = byStatus[t.key].length;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                style={{
                  minHeight: 44, padding: '0 18px',
                  display: 'inline-flex', alignItems: 'center', gap: 9,
                  borderRadius: 11, fontSize: 14, fontFamily: 'inherit',
                  fontWeight: active ? 600 : 500,
                  cursor: 'pointer',
                  border: active ? '1.5px solid rgba(75,166,234,0.35)' : '1px solid #e8eaed',
                  color: active ? '#2e8fd4' : '#6b7280',
                  background: active ? '#fff' : 'rgba(255,255,255,0.6)',
                  boxShadow: active ? '0 2px 10px rgba(75,166,234,0.14)' : 'none',
                  transition: 'all 140ms ease',
                }}
              >
                {t.label}
                <span style={{
                  fontSize: 11.5, fontWeight: 600,
                  padding: '2px 8px', borderRadius: 20, minWidth: 22, textAlign: 'center',
                  color: active ? '#2e8fd4' : '#9ca3af',
                  background: active ? 'rgba(75,166,234,0.14)' : '#f1f3f5',
                }}>
                  {loading ? '…' : count}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      {!REVIEW_WEBHOOK_URL && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '11px 15px', marginBottom: 18,
          borderRadius: 11, background: 'rgba(249,115,22,0.07)',
          border: '1px solid rgba(249,115,22,0.22)',
          fontSize: 12.5, color: '#b45309',
        }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
            <path d="M12 8v4M12 16h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          Webhook not configured — set <code style={{ fontFamily: 'var(--font-mono)', background: 'rgba(180,83,9,0.1)', borderRadius: 4, padding: '1px 5px', margin: '0 2px' }}>REVIEW_WEBHOOK_URL</code> to enable sending. Rating still works.
        </div>
      )}

      {/* Toolbar */}
      <div className="gr-toolbar">
        <div className="gr-search">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" style={{
            position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none',
          }}>
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="1.8" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search name, phone, plate, booking or nationality…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{
              paddingLeft: 38, paddingRight: 14, height: 44,
              border: '1px solid #e8eaed', borderRadius: 11,
              fontSize: 13.5, color: '#0f1117', background: '#fff',
              outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: 'inherit',
              boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
            }}
          />
        </div>

        {tab === 'awaiting' && byStatus.awaiting.length > 0 && (
          <SendButton
            label={`Send All (${byStatus.awaiting.length})`}
            busy={bulkSending}
            onClick={handleSendAll}
          />
        )}

        {!loading && (
          <span style={{ marginLeft: 'auto', fontSize: 12.5, color: '#9ca3af' }}>
            {visible.length} of {byStatus[tab].length} shown
          </span>
        )}
      </div>

      {error && (
        <div style={{
          padding: '11px 15px', marginBottom: 16, borderRadius: 11,
          background: '#fef2f2', border: '1px solid rgba(239,68,68,0.2)',
          fontSize: 13, color: '#ef4444',
        }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div style={{
        background: '#fff', borderRadius: 16,
        border: '1px solid #e8eaed',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        overflow: 'hidden',
      }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 1080 }}>
            <thead>
              <tr>
                <Th>Customer</Th>
                {tab !== 'rate' && <Th sortKey="our_rating" sort={sort} onSort={toggleSort}>Our Rating</Th>}
                <Th sortKey="nationality" sort={sort} onSort={toggleSort}>Nationality</Th>
                {tab === 'rate' && <Th sortKey="age" sort={sort} onSort={toggleSort}>Age</Th>}
                <Th sortKey="phone" sort={sort} onSort={toggleSort}>Phone</Th>
                <Th sortKey="plate_number" sort={sort} onSort={toggleSort}>Plate</Th>
                {tab !== 'sent' && <Th sortKey="booking_end_date" sort={sort} onSort={toggleSort}>Booking</Th>}
                {tab === 'sent' && <Th>Sent At</Th>}
                {tab === 'rate'
                  ? <Th align="right" sortKey="our_rating" sort={sort} onSort={toggleSort}>Rating</Th>
                  : <Th align="right">Action</Th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: colCount }).map((__, j) => (
                      <td key={j} style={td}>
                        <div style={{ height: 13, borderRadius: 6, background: '#f1f3f5', width: j === 0 ? 160 : 90, animation: 'pulse 1.4s ease-in-out infinite' }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : visible.length === 0 ? (
                <tr>
                  <td colSpan={colCount} style={{ padding: '64px 0', textAlign: 'center' }}>
                    <div style={{
                      width: 52, height: 52, borderRadius: 14,
                      background: 'linear-gradient(135deg, rgba(75,166,234,0.1) 0%, rgba(75,166,234,0.06) 100%)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      margin: '0 auto 16px',
                    }}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ color: '#3A74C2' }}>
                        <path d="M12 2.6l2.9 5.9 6.5.95-4.7 4.58 1.11 6.47L12 17.45l-5.81 3.05 1.11-6.47-4.7-4.58 6.5-.95L12 2.6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
                      </svg>
                    </div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#374151', marginBottom: 5 }}>
                      {search ? 'No matching customers'
                        : tab === 'rate' ? 'Nothing left to rate'
                        : tab === 'awaiting' ? 'No customers awaiting send'
                        : 'No requests sent yet'}
                    </div>
                    <div style={{ fontSize: 13, color: '#9ca3af' }}>
                      {search ? 'Try a different search term'
                        : tab === 'rate' ? 'Every completed customer has been rated'
                        : tab === 'awaiting' ? 'Rate a customer 5 stars to queue them here'
                        : 'Sent review requests will appear here'}
                    </div>
                  </td>
                </tr>
              ) : (
                visible.map((row, idx) => (
                  <tr
                    key={row.customer_id}
                    className="gr-row"
                    style={{ borderTop: idx === 0 ? 'none' : '1px solid #f2f4f6' }}
                  >
                    <td style={td}><CustomerCell name={row.full_name} email={row.email} /></td>
                    {tab !== 'rate' && (
                      <td style={td}><StarsDisplay value={row.our_rating} /></td>
                    )}
                    <td style={td}><NationalityCell nationality={row.nationality} /></td>
                    {tab === 'rate' && (
                      <td style={{ ...td, fontSize: 13, color: row.age == null ? '#d1d5db' : '#374151', whiteSpace: 'nowrap' }}>
                        {dash(row.age)}
                      </td>
                    )}
                    <td style={td}><PhoneCell phone={row.phone} /></td>
                    <td style={td}><PlateCell plate={row.plate_number} model={row.model_name} /></td>
                    {tab !== 'sent' && (
                      <td style={td}>
                        <BookingCell start={row.booking_start_date} end={row.booking_end_date} number={row.latest_booking_number} />
                      </td>
                    )}
                    {tab === 'sent' && (
                      <td style={{ ...td, fontSize: 12.5, color: '#6b7280', whiteSpace: 'nowrap' }}>
                        {formatDateTime(row.review_sent_at)}
                      </td>
                    )}
                    <td style={{ ...td, textAlign: 'right' }}>
                      {tab === 'rate' && (
                        <StarRating
                          value={row.our_rating}
                          busy={ratingId === row.customer_id}
                          onRate={rating => handleRate(row, rating)}
                        />
                      )}
                      {tab === 'awaiting' && (
                        <SendButton
                          label="Send Review Request"
                          busy={sendingId === row.customer_id || bulkSending}
                          onClick={() => promptLanguage(row, 'send')}
                        />
                      )}
                      {tab === 'sent' && (
                        <SendButton
                          label="Resend"
                          variant="ghost"
                          busy={sendingId === row.customer_id}
                          onClick={() => promptLanguage(row, 'resend')}
                        />
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {langPrompt && (
        <LanguageModal
          customerName={langPrompt.row.full_name}
          mode={langPrompt.mode}
          busy={sendingId === langPrompt.row.customer_id}
          onPick={language => {
            if (langPrompt.mode === 'send') void handleSend(langPrompt.row, language);
            else void handleResend(langPrompt.row, language);
          }}
          onClose={() => setLangPrompt(null)}
        />
      )}

      {toast && <Toast message={toast.message} kind={toast.kind} />}
    </div>
  );
};

export default GoogleReviewsPage;
